import { useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useIsEmbeddedWebView } from '@hungryroot/environment';
import { AuthState, useIdentify } from '@hungryroot/rest-hooks';
import { buildClientForceVariantEnv } from '../client-side-enrollment/buildClientForceVariantEnv';
import { getExperimentEnrollment } from '../client-side-enrollment/enroll';
import { getPersistedEnrollment } from '../client-side-enrollment/persist';
import {
  ExperimentDefinitions,
  type ExperimentVariants,
  type Experiments,
} from '../experiments';
import { getExperimentGroup } from '../query/get-experiment-group';
import {
  type ExperimentFlagsObject,
  type ExperimentHookOptions,
  type GetVariants,
  type UseExperimentFlagsResult,
} from '../types';
import { useCustomerExperiments } from './useCustomerExperiments';

/**
 * Returns a referentially stable array as long as the elements haven't changed.
 * Avoids JSON.stringify for dependency comparison.
 */
function useStableArray<T>(arr: readonly T[]): readonly T[] {
  const ref = useRef(arr);
  if (
    arr.length !== ref.current.length ||
    arr.some((v, i) => v !== ref.current[i])
  ) {
    ref.current = arr;
  }
  return ref.current;
}

/**
 * Generate flags object for a single experiment.
 */
export function generateExperimentFlags<E extends Experiments>(
  expId: E,
  variant: number | undefined
): ExperimentFlagsObject<GetVariants<E>> {
  const definition = ExperimentDefinitions[expId];
  const variants = definition.variants;

  // Whether a real enrollment record exists, computed from the RAW variant
  // before the control-coercion below discards that distinction. No record
  // (undefined) and exclusion/holdout codes (negative) are NOT enrollment;
  // only a genuine variant >= 0 from the backend/anonymous lookup counts.
  const isEnrolled = variant !== undefined && variant >= 0;

  // Treat undefined and exclusion codes (negative) as control (0)
  const effectiveVariant = variant === undefined || variant < 0 ? 0 : variant;

  // Determine if user is in any treatment variant (> 0)
  const isVariant = effectiveVariant > 0;

  // Build the result object
  const result: Record<string, boolean | number> = {
    isVariant,
    isEnrolled,
    variant: effectiveVariant,
  };

  // Add individual variant flags (excluding control = 0).
  // Some declared arms use labels ≥10 (Statsig / admin); clients only ever
  // see `variant_modulo` (ones digit). Remap so e.g. `isVariant12` tracks
  // effective variant 2.
  for (const v of variants) {
    if (v !== 0) {
      const matchedVariant = VARIANT_FLAG_EFFECTIVE_REMAP[v] ?? v;
      result[`isVariant${v}`] = effectiveVariant === matchedVariant;
    }
  }

  return result as ExperimentFlagsObject<GetVariants<E>>;
}

/**
 * Declared variant label → effective `variant_modulo` value the client sees.
 * Keep the `isVariant{label}` flag name; match against the ones-digit arm.
 * Keyed by label for every experiment, so a label means the same thing
 * wherever it's declared (Exp 195 uses 12, Exp 220 uses 13).
 */
const VARIANT_FLAG_EFFECTIVE_REMAP: Readonly<Record<number, number>> = {
  12: 2,
  13: 3,
  14: 4,
};

/**
 * Mask to extract the variant number from encoded iteration values.
 * Raw variant values encode the iteration in the tens digit (e.g., 11, 21, 32).
 * Modulo 10 strips the iteration to get the actual variant (1, 1, 2).
 */
const EXPERIMENT_ITERATION_MASK = 10;

/**
 * Hook to get boolean flags for one or more experiments.
 * Supports both pre-signup (anonymous) and post-signup (authenticated) users.
 *
 * For anonymous users, this hook will automatically enroll them in pre-signup experiments.
 * For authenticated users, it reads from the customer experiments API.
 *
 * Pass `{ enroll: false }` to CHECK enrollment without CAUSING it. An unenrolled
 * visitor then reads `isEnrolled: false` / `variant: 0`, and no variant is
 * rolled, no cookie is written, and no enrollment is tracked. Use this for
 * checks at the END of an experiment flow, where a visitor who skipped the flow
 * must not be counted. The option is a no-op for authenticated users, whose
 * enrollment is read from the backend rather than created locally.
 *
 * CONSTRAINT — one mode per experiment per surface: a non-enrolling check is a
 * point-in-time cookie read. Its effect deps do not change when a cookie is
 * written elsewhere, so it will NOT self-correct. Never pair an enrolling and a
 * non-enrolling check for the same experiment on the same surface; the
 * non-enrolling one can latch `isEnrolled: false` for the life of the mount.
 *
 * Returns a discriminated union on `isLoading`:
 * - When `isLoading: true`, each experiment key is `null`
 * - When `isLoading: false`, each experiment key contains:
 *   - `isVariant`: true if user is in any treatment variant (variant > 0)
 *   - `isVariantN`: true if user is in variant N (for each variant > 0)
 *   - `variant`: the raw variant number (0 for control)
 *   - `isEnrolled`: true only if a real enrollment record exists — false for
 *     no record, a failed lookup, or an exclusion/holdout code, even though
 *     `variant` reads 0 in all of those cases too
 *
 * @example
 * // Single experiment
 * const result = useExperimentFlags(224);
 * if (!result.isLoading) {
 *   if (result.cartExplainerExp224.isVariant) { ... }
 * }
 *
 * @example
 * // Multiple experiments in one call
 * const result = useExperimentFlags([224, 186]);
 * if (!result.isLoading) {
 *   if (result.cartExplainerExp224.isVariant2) { ... }
 *   if (result.ftcRound3Exp186.isVariant) { ... }
 * }
 */
export function useExperimentFlags<
  const T extends Experiments | readonly Experiments[],
>(
  expIdOrIds: T,
  options?: Pick<ExperimentHookOptions, 'enroll'>
): UseExperimentFlagsResult<
  T extends readonly Experiments[] ? T : readonly [T]
> {
  const { enroll = true } = options ?? {};
  const { authState } = useIdentify();
  const isAuthLoading = authState === AuthState.isLoading;
  const isAnonymous = authState === AuthState.isAnonymous;

  const queryClient = useQueryClient();
  const isEmbeddedWebView = useIsEmbeddedWebView();

  // Normalize to array with stable reference
  const normalized = (
    Array.isArray(expIdOrIds) ? expIdOrIds : [expIdOrIds]
  ) as readonly Experiments[];
  const expIds = useStableArray(normalized);

  // For anonymous users, track pre-signup enrollment state.
  //
  // Seeded synchronously from the enrollment cookie so a visitor who is
  // already enrolled doesn't get a loading frame while the effect below runs.
  // The cookie read is all that's needed: `getExperimentEnrollment`'s
  // `enrolled` / `opted-out` branches are exactly this lookup plus a sign
  // check, and its `env` / `queryClient` arguments are used only inside the
  // async `enroll()` it returns for the `not-enrolled` case.
  //
  // Variants are stored iteration-encoded and are left that way here — the
  // memo below strips the iteration, matching what the effect records.
  //
  // Deliberately does NOT record `null` for a missing enrollment. Seeding
  // `null` would resolve `isLoading` for a visitor who has no enrollment yet,
  // pre-empting the effect's decision to either enroll them or (under
  // `enroll: false`) record the absence.
  const [presignupVariants, setPresignupVariants] = useState<
    Record<number, ExperimentVariants<Experiments> | null>
  >(() => {
    const seeded: Record<number, ExperimentVariants<Experiments>> = {};

    for (const expId of expIds) {
      const persisted = getPersistedEnrollment(expId);

      if (persisted !== undefined) {
        seeded[expId] = persisted.variant as ExperimentVariants<Experiments>;
      }
    }

    return seeded;
  });
  const [isEnrolling, setIsEnrolling] = useState(false);

  // Handle pre-signup enrollment for anonymous users
  useEffect(() => {
    if (!isAnonymous) {
      return;
    }

    const abortController = new AbortController();

    const env = buildClientForceVariantEnv({ isEmbeddedWebView });
    const enrollmentStates = expIds.map((expId) =>
      getExperimentEnrollment({
        expId,
        queryClient,
        env,
      })
    );

    // Check for synchronous variants (already enrolled).
    // Permits `null` so the `enroll: false` path can record "checked, and there
    // is no enrollment" — which resolves `isLoading` without creating one.
    const syncVariants: Record<number, ExperimentVariants<Experiments> | null> =
      {};
    const pendingEnrollments: Array<{
      expId: Experiments;
      enroll: () => Promise<ExperimentVariants<Experiments> | null>;
    }> = [];

    enrollmentStates.forEach((enrollmentState, index) => {
      const expId = expIds[index];
      if (
        enrollmentState.state === 'enrolled' ||
        enrollmentState.state === 'opted-out'
      ) {
        syncVariants[expId] = enrollmentState.variant;
      } else if (enrollmentState.state === 'not-enrolled') {
        if (enroll) {
          pendingEnrollments.push({ expId, enroll: enrollmentState.enroll });
        } else {
          // Record the absence instead of enrolling. Flows through the memo
          // below as `variant === undefined`, yielding `isEnrolled: false` and
          // `variant: 0`.
          syncVariants[expId] = null;
        }
      }
    });

    // Update with synchronous variants
    if (Object.keys(syncVariants).length > 0) {
      setPresignupVariants((prev) => ({ ...prev, ...syncVariants }));
    }

    // Trigger async enrollments
    if (pendingEnrollments.length > 0) {
      setIsEnrolling(true);

      const enrollAll = async () => {
        try {
          const results = await Promise.all(
            pendingEnrollments.map(async ({ expId, enroll }) => {
              const variant = await enroll();
              return { expId, variant };
            })
          );

          if (abortController.signal.aborted) return;

          const newVariants: Record<
            number,
            ExperimentVariants<Experiments> | null
          > = {};
          results.forEach(({ expId, variant }) => {
            newVariants[expId] = variant;
          });
          setPresignupVariants((prev) => ({ ...prev, ...newVariants }));
        } finally {
          if (!abortController.signal.aborted) setIsEnrolling(false);
        }
      };

      void enrollAll();
    }

    return () => {
      abortController.abort();
      setIsEnrolling(false);
    };
  }, [isAnonymous, expIds, queryClient, isEmbeddedWebView, enroll]);

  // For authenticated users, fetch from customer experiments API
  const { data: experimentsData, isLoading: isDataLoading } =
    useCustomerExperiments();

  return useMemo(() => {
    const isLoading =
      isAuthLoading ||
      (isAnonymous
        ? isEnrolling || expIds.some((id) => !(id in presignupVariants))
        : isDataLoading);

    const result: Record<string, unknown> = { isLoading };

    for (const expId of expIds) {
      const definition = ExperimentDefinitions[expId];
      const varKey = `${definition.name}Exp${expId}`;

      if (isLoading) {
        result[varKey] = null;
        continue;
      }

      // Get variant based on auth state
      let variant: number | undefined;

      if (isAnonymous) {
        // For anonymous users, use pre-signup enrollment.
        // Raw variant values encode the iteration in the tens digit
        // (e.g., 11 = iteration 1 variant 1, 32 = iteration 3 variant 2).
        // The modulo operation strips the iteration to get the actual variant,
        // matching the variant_modulo field from the API and the
        // behavior in useClientSidePresignupExperiment.
        const presignupVariant = presignupVariants[expId];
        if (presignupVariant != null) {
          variant = presignupVariant % EXPERIMENT_ITERATION_MASK;
        }
      } else if (experimentsData?.experiments) {
        // For authenticated users, use customer experiments API
        variant = getExperimentGroup(expId, experimentsData);
      }

      result[varKey] = generateExperimentFlags(expId, variant);
    }

    return result as UseExperimentFlagsResult<
      T extends readonly Experiments[] ? T : readonly [T]
    >;
  }, [
    expIds,
    isAuthLoading,
    isAnonymous,
    isEnrolling,
    isDataLoading,
    presignupVariants,
    experimentsData,
  ]);
}
